import { IpnrMap } from '..';
import { Subject } from 'rxjs';
import { DashboardService, DashletSettingsService } from '@services/index';
import { Customer, Dashlet } from '@models/index';

export class MosDataPoint {
    y!: number;
    x!: Date;
    total!: number;
}

class MosScoreCount {
    roundedScore!: number;
    count!: number;
}

export class DashletVoiceQualityMeanOpinionScoreScatter extends Dashlet {
    public readonly commandTypeIdMosIncrement = '9343C956-3F20-4451-8ADF-D19656E7F0FD';
    public readonly commandTypeIdIpnrLookup = '142e7a84-1b04-4c59-a645-9b9b1e5b5312';
    public customer!: Customer;
    public locationId!: string;
    public equipmentIds!: string[];

    public ipnrMap: IpnrMap = new IpnrMap();

    readonly interval = 30;

    private recentScoresNew: Map<number, Map<string, MosScoreCount[]>> = new Map();
    private backfillStore: Map<number, Map<string, MosScoreCount[]>> = new Map();
    private lastProducedTime = 0;
    public mosDataGood: MosDataPoint[] = [];
    public mosDataSatisfactory: MosDataPoint[] = [];
    public mosDataPoor: MosDataPoint[] = [];
    public mosDataUnknown: MosDataPoint[] = [];

    public currentDataGood = 0;
    public currentDataSatisfactory = 0;
    public currentDataPoor = 0;
    public currentDataUnknown = 0;
    public equipmentLoaded: Subject<null> = new Subject();
    private dataUpdated: Subject<null> = new Subject();
    private isMosDataStale = false;
    get onDataUpdated() {
        return this.dataUpdated;
    }

    private mosDataTimeoutHandler: ReturnType<typeof setTimeout> | null = null;
    private lastMosTimestamp: Date | null = null;

    constructor(private dashboardService: DashboardService, private settingsService: DashletSettingsService) {
        super();

        this.sizes = [
            {
                id: 0,
                label: 'Small',
                cols: 5,
                rows: 5
            },
            {
                id: 1,
                label: 'Large',
                cols: 10,
                rows: 5
            }
        ];
        this.applySize(0);

        // delay the polling by 1 interval to account for updates lagging behind real-time
        this.dashboardService.getUpdateInterval(this.interval).subscribe(time => {
            this.updateMosDataNew(new Date(time.getTime() - 30 * 1000));
        });
    }

    applySettings(v: { [key: string]: any }) {
        super.applySettings(v);

        this.configured = v.customer && v.location && v.equipment;

        if (v.customer) {
            this.customer = new Customer(v.customer.value, v.customer.label);
        } else {
            this.customer = new Customer('', '');
        }
        this.locationId = v.location && v.location.value ? v.location.value : null;
        const equipValue: string = v.equipment && v.equipment.value ? v.equipment.value : null;

        this.generatedNameTag =
            v.location && v.equipment ? `${v.location.label} | ${v.equipment.label}` : 'Unconfigured';
        this.customNameTag = v.nameTag;

        if (equipValue && this.locationId) {
            this.configured = true;
            if (equipValue === '=all=') {
                this.settingsService.getRtcpReceivers(this.locationId).subscribe(res => {
                    if (res) {
                        this.equipmentIds = res.map(receiver => receiver.value);
                        this.equipmentLoaded.next(null);
                    }
                });
            } else {
                this.equipmentIds = [equipValue];
                this.equipmentLoaded.next(null);
            }
        }
    }

    dispose() {}

    resetData() {
        this.mosDataGood = [];
        this.mosDataSatisfactory = [];
        this.mosDataPoor = [];
        this.mosDataUnknown = [];
        this.recentScoresNew.clear();
        this.backfillStore.clear();
        this.currentDataGood = 0;
        this.currentDataSatisfactory = 0;
        this.currentDataPoor = 0;
        this.currentDataUnknown = 0;

        this.dataUpdated.next(null);
    }

    public processRtcpDataNew(key: string, data: any[]) {
        if (!data || data.length === 0) return;
        const timestampStr = data?.[0]?.timestamp;
        this.lastMosTimestamp = timestampStr ? new Date(timestampStr) : new Date();

        const roundedTimestamp = new Date(this.lastMosTimestamp);
        roundedTimestamp.setSeconds(Math.ceil(roundedTimestamp.getSeconds() / this.interval) * this.interval, 0);
        const timeKey = roundedTimestamp.getTime();

        let sources = this.recentScoresNew.get(timeKey);
        if (!sources) {
            sources = new Map<string, MosScoreCount[]>();
            this.recentScoresNew.set(timeKey, sources);
        }

        const scores: MosScoreCount[] = [];
        const rowData = data?.[0]?.data ?? [];
        rowData.forEach(item => {
            if (+item.Count > 0) {
                scores.push({
                    roundedScore: +item.MosInterval.split('_')[2],
                    count: +item.Count
                });
            }
        });

        sources.set(key, scores);
        this.resetMosDataTimeout();
    }

    public startBackfill(): void {
        this.backfillStore.clear();
    }

    public processRtcpBackfill(sourceKey: string, docs: any[], from: Date): void {
        if (!docs || docs.length === 0) return;

        const earliest = new Date();
        if (this.getSize().id === 0) {
            earliest.setMinutes(earliest.getMinutes() - 30);
        } else if (this.getSize().id === 1) {
            earliest.setMinutes(earliest.getMinutes() - 60);
        }
        const lowerBound = Math.max(from.getTime(), earliest.getTime());

        const sortedDocs = docs
            .filter(doc => doc?.timestamp)
            .sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp));

        const producedKeys = new Set<number>();
        sortedDocs.forEach(doc => {
            // sorted oldest to earliest already but now round up to the nearest polling interval
            const roundedTimestamp = new Date(doc.timestamp);
            roundedTimestamp.setSeconds(Math.ceil(roundedTimestamp.getSeconds() / this.interval) * this.interval, 0);
            const timeKey = roundedTimestamp.getTime();
            if (timeKey <= lowerBound) return;

            const scores: MosScoreCount[] = [];
            (doc.data ?? []).forEach(item => {
                if (+item.Count > 0) {
                    scores.push({
                        roundedScore: +item.MosInterval.split('_')[2],
                        count: +item.Count
                    });
                }
            });

            if (timeKey > this.lastProducedTime) {
                // graph hasn't drawn this point yet so add it to the recentScoresNew map (live object)
                let sources = this.recentScoresNew.get(timeKey);
                if (!sources) {
                    sources = new Map<string, MosScoreCount[]>();
                    this.recentScoresNew.set(timeKey, sources);
                }
                if (!sources.has(sourceKey)) {
                    sources.set(sourceKey, scores);
                }
                return;
            }

            // otherwise, we add it to the backfillStore for redrawing it after this foreach
            let sources = this.backfillStore.get(timeKey);
            if (!sources) {
                sources = new Map<string, MosScoreCount[]>();
                this.backfillStore.set(timeKey, sources);
            }
            sources.set(sourceKey, scores);
            producedKeys.add(timeKey);
        });

        if (producedKeys.size === 0) return;

        const isNotProduced = (p: MosDataPoint) => !producedKeys.has(p.x.getTime());
        const newGood = this.mosDataGood.filter(isNotProduced);
        const newSatisfactory = this.mosDataSatisfactory.filter(isNotProduced);
        const newPoor = this.mosDataPoor.filter(isNotProduced);
        const newUnknown = this.mosDataUnknown.filter(isNotProduced);

        producedKeys.forEach(timeKey => {
            const x = new Date(timeKey);
            for (const sourceScores of this.backfillStore.get(timeKey)!.values()) {
                sourceScores.forEach(score => {
                    const point: MosDataPoint = { x, y: score.roundedScore / 10, total: score.count };
                    if (point.y >= 4.0) {
                        newGood.push(point);
                    } else if (point.y >= 3.6) {
                        newSatisfactory.push(point);
                    } else if (point.y >= 0) {
                        newPoor.push(point);
                    } else {
                        newUnknown.push(point);
                    }
                });
            }
        });

        const byTime = (a: MosDataPoint, b: MosDataPoint) => a.x.getTime() - b.x.getTime();
        this.mosDataGood = newGood.sort(byTime);
        this.mosDataSatisfactory = newSatisfactory.sort(byTime);
        this.mosDataPoor = newPoor.sort(byTime);
        this.mosDataUnknown = newUnknown.sort(byTime);

        this.dataUpdated.next(null);
    }

    private resetMosDataTimeout() {
        if (this.mosDataTimeoutHandler) {
            clearTimeout(this.mosDataTimeoutHandler);
        }
        this.mosDataTimeoutHandler = setTimeout(() => {
            if (this.lastMosTimestamp) {
                const now = new Date();
                const diff = now.getTime() - this.lastMosTimestamp.getTime();
                if (diff >= 30000) {
                    this.isMosDataStale = true;
                }
            }
        }, 30000);
        this.isMosDataStale = false;
    }

    private updateMosDataNew(time: Date) {
        this.lastProducedTime = time.getTime();
        if (this.isMosDataStale) {
            this.dataUpdated.next(null);
            this.currentDataGood = 0;
            this.currentDataSatisfactory = 0;
            this.currentDataPoor = 0;
            this.currentDataUnknown = 0;
            return;
        }

        const before = new Date(time);
        if (this.getSize().id === 0) {
            before.setMinutes(before.getMinutes() - 30);
        } else if (this.getSize().id === 1) {
            before.setMinutes(before.getMinutes() - 60);
        }

        const truncateExpr = (p: MosDataPoint) => p.x.valueOf() >= before.valueOf();
        const newUnknown = this.mosDataUnknown.filter(truncateExpr);
        const newGood = this.mosDataGood.filter(truncateExpr);
        const newSatisfactory = this.mosDataSatisfactory.filter(truncateExpr);
        const newPoor = this.mosDataPoor.filter(truncateExpr);

        const unknownExpr = (p: MosDataPoint) => p.y < 0;
        const goodExpr = (p: MosDataPoint) => p.y >= 4.0;
        const satisfactoryExpr = (p: MosDataPoint) => p.y >= 3.6 && p.y < 4.0;
        const poorExpr = (p: MosDataPoint) => p.y >= 0 && p.y < 3.6;

        this.currentDataGood = 0;
        this.currentDataSatisfactory = 0;
        this.currentDataPoor = 0;
        this.currentDataUnknown = 0;

        const totalRecords: MosScoreCount[] = [];
        const currentScores = this.recentScoresNew.get(time.getTime());
        if (currentScores) {
            for (const sourceScores of currentScores.values()) {
                totalRecords.push(...sourceScores);
            }
            this.recentScoresNew.delete(time.getTime());
        }

        totalRecords.forEach(score => {
            const point: MosDataPoint = {
                x: time,
                y: score.roundedScore / 10,
                total: score.count
            };
            if (goodExpr(point)) {
                newGood.push(point);
                this.currentDataGood += score.count;
            }
            if (satisfactoryExpr(point)) {
                newSatisfactory.push(point);
                this.currentDataSatisfactory += score.count;
            }
            if (poorExpr(point)) {
                newPoor.push(point);
                this.currentDataPoor += score.count;
            }
            if (unknownExpr(point)) {
                newUnknown.push(point);
                this.currentDataUnknown += score.count;
            }
        });

        this.mosDataGood = newGood;
        this.mosDataSatisfactory = newSatisfactory;
        this.mosDataPoor = newPoor;
        this.mosDataUnknown = newUnknown;

        this.dataUpdated.next(null);
    }
}
